import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["container", "first", "divider", "second"];
  static values = {
    direction: String,
    minSize: { type: Number, default: 100 }, // Minimum size in pixels
    initialRatio: { type: Number, default: 0.7 }, // Default ratio for the first panel
  };

  connect() {
    this.isResizing = false;
    this.currentY = 0;
    this.currentX = 0;
    this.initialHeight = 0;
    this.initialWidth = 0;
    this.initialOffset = 0;

    // Bind methods to this instance
    this.mouseDown = this.mouseDown.bind(this);
    this.mouseMove = this.mouseMove.bind(this);
    this.mouseUp = this.mouseUp.bind(this);

    // Add global event listeners
    document.addEventListener("mousemove", this.mouseMove);
    document.addEventListener("mouseup", this.mouseUp);
    document.addEventListener("mouseleave", this.mouseUp);

    // Set initial sizes once after a short delay to ensure DOM is ready
    setTimeout(() => this.initializeSizes(), 50);
  }

  initializeSizes() {
    // Set explicit pixel sizes based on current dimensions
    if (this.directionValue === "vertical") {
      const containerHeight = this.containerTarget.clientHeight;
      let firstHeight = Math.round(containerHeight * 0.6); // Default to 60%

      // If the first panel already has a height, use it
      const currentHeight = this.firstTarget.offsetHeight;
      if (currentHeight > 0) {
        firstHeight = currentHeight;
      }

      // Make sure it fits within min constraints
      const dividerHeight = this.dividerTarget.offsetHeight;
      firstHeight = Math.max(this.minSizeValue, firstHeight);
      firstHeight = Math.min(
        containerHeight - this.minSizeValue - dividerHeight,
        firstHeight
      );

      // Apply heights
      this.firstTarget.style.height = `${firstHeight}px`;
      this.secondTarget.style.height = `${
        containerHeight - firstHeight - dividerHeight
      }px`;
      this.secondTarget.style.flexGrow = 0; // Prevent flex from interfering with explicit height
    } else {
      const containerWidth = this.containerTarget.clientWidth;
      let firstWidth = Math.round(containerWidth * 0.5); // Default to 50%

      // If the first panel already has a width, use it
      const currentWidth = this.firstTarget.offsetWidth;
      if (currentWidth > 0) {
        firstWidth = currentWidth;
      }

      // Make sure it fits within min constraints
      const dividerWidth = this.dividerTarget.offsetWidth;
      firstWidth = Math.max(this.minSizeValue, firstWidth);
      firstWidth = Math.min(
        containerWidth - this.minSizeValue - dividerWidth,
        firstWidth
      );

      // Apply widths
      this.firstTarget.style.width = `${firstWidth}px`;
      this.secondTarget.style.width = `${
        containerWidth - firstWidth - dividerWidth
      }px`;
      this.secondTarget.style.flexGrow = 0; // Prevent flex from interfering with explicit width
    }
  }

  disconnect() {
    // Remove global event listeners
    document.removeEventListener("mousemove", this.mouseMove);
    document.removeEventListener("mouseup", this.mouseUp);
    document.removeEventListener("mouseleave", this.mouseUp);
  }

  mouseDown(event) {
    event.preventDefault();
    this.isResizing = true;

    // Record the exact mouse position
    this.currentY = event.clientY;
    this.currentX = event.clientX;

    // Get the current dimensions
    this.initialHeight = this.firstTarget.offsetHeight;
    this.initialWidth = this.firstTarget.offsetWidth;

    // Record container offset for more accurate calculations
    this.containerRect = this.containerTarget.getBoundingClientRect();

    // Fix the size of the container during resize to prevent layout shifts
    const containerStyle = window.getComputedStyle(this.containerTarget);
    this.containerTarget.style.height = containerStyle.height;
    this.containerTarget.style.width = containerStyle.width;

    // Add cursor styles to indicate resizing
    if (this.directionValue === "vertical") {
      document.body.style.cursor = "row-resize";
    } else {
      document.body.style.cursor = "col-resize";
    }

    // Prevent text selection during resize
    document.body.classList.add("resizing");
    this.containerTarget.classList.add("resizing");
  }

  mouseMove(event) {
    if (!this.isResizing) return;

    // Calculate the position delta
    const deltaY = event.clientY - this.currentY;
    const deltaX = event.clientX - this.currentX;

    if (this.directionValue === "vertical") {
      // Calculate new height
      let newHeight = this.initialHeight + deltaY;
      const containerHeight = this.containerTarget.offsetHeight;
      const dividerHeight = this.dividerTarget.offsetHeight;

      // Apply min size constraint
      newHeight = Math.max(this.minSizeValue, newHeight);
      newHeight = Math.min(
        containerHeight - this.minSizeValue - dividerHeight,
        newHeight
      );

      // Update panel heights with pixel values for precision
      this.firstTarget.style.height = `${newHeight}px`;
      this.secondTarget.style.height = `${
        containerHeight - newHeight - dividerHeight
      }px`;
    } else {
      // Calculate new width
      let newWidth = this.initialWidth + deltaX;
      const containerWidth = this.containerTarget.offsetWidth;
      const dividerWidth = this.dividerTarget.offsetWidth;

      // Apply min size constraint
      newWidth = Math.max(this.minSizeValue, newWidth);
      newWidth = Math.min(
        containerWidth - this.minSizeValue - dividerWidth,
        newWidth
      );

      // Update panel widths with pixel values
      this.firstTarget.style.width = `${newWidth}px`;
      this.secondTarget.style.width = `${
        containerWidth - newWidth - dividerWidth
      }px`;
    }
  }

  mouseUp() {
    if (!this.isResizing) return;

    this.isResizing = false;

    // Reset cursor and styles
    document.body.style.cursor = "";
    document.body.classList.remove("resizing");
    this.containerTarget.classList.remove("resizing");

    // Remove the fixed size constraint on the container
    this.containerTarget.style.height = "";
    this.containerTarget.style.width = "";
  }
}
